/**
 * Converts Excalidraw diagram elements to a human-readable text description
 * for the voice agent to reference during systems design interviews.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExcalidrawElement = any;

function getElementLabel(el: ExcalidrawElement, idToLabel: Map<string, string>): string {
  if (!el || typeof el !== "object") return "?";
  const id = el.id;
  // Prefer explicit text content
  if (el.type === "text" && typeof el.text === "string" && el.text.trim()) {
    return el.text.trim();
  }
  // Frame names (Excalidraw frames can have a name)
  if (el.type === "frame" && typeof el.name === "string" && el.name.trim()) {
    return el.name.trim();
  }
  // Container labels (text inside shapes)
  if (el.type === "text" && el.containerId && idToLabel.has(el.containerId)) {
    const containerLabel = idToLabel.get(el.containerId);
    if (typeof el.text === "string" && el.text.trim()) {
      return el.text.trim();
    }
    return containerLabel || "?";
  }
  // Arrow labels
  if (el.type === "arrow" && typeof el.label?.text === "string" && el.label.text.trim()) {
    return el.label.text.trim();
  }
  // Fallback: use stored label for this id
  if (idToLabel.has(id)) return idToLabel.get(id)!;
  // Shapes without text: use type as hint
  if (["rectangle", "ellipse", "diamond", "frame"].includes(el.type)) {
    return `[${el.type}]`;
  }
  return "?";
}

/**
 * Convert Excalidraw elements to a concise text description for the agent.
 * Returns empty string if no meaningful content.
 */
export function diagramElementsToText(
  rawElements: unknown
): string {
  let elements: ExcalidrawElement[] = [];
  if (Array.isArray(rawElements)) {
    elements = rawElements.filter((el) => el != null && typeof el === "object");
  } else if (rawElements && typeof rawElements === "object" && !Array.isArray(rawElements)) {
    elements = Object.values(rawElements).filter((el) => el != null && typeof el === "object");
  }
  if (elements.length === 0) return "";

  const idToLabel = new Map<string, string>();

  // First pass: collect text labels (standalone text, text in containers)
  for (const el of elements) {
    if (el.type === "text" && typeof el.text === "string" && el.text.trim()) {
      const label = el.text.trim();
      idToLabel.set(el.id, label);
      if (el.containerId) {
        idToLabel.set(el.containerId, label);
      }
    }
  }

  // Second pass: label shapes that have bound text (text with containerId pointing to shape)
  for (const el of elements) {
    if (el.type === "text" && el.containerId && !idToLabel.has(el.containerId)) {
      const label = typeof el.text === "string" && el.text.trim() ? el.text.trim() : `[shape]`;
      idToLabel.set(el.containerId, label);
    }
  }

  const components = new Set<string>();
  const connections: string[] = [];

  // Collect component names (non-arrow elements with labels)
  for (const el of elements) {
    if (el.type === "arrow") continue;
    const label = getElementLabel(el, idToLabel);
    if (label && label !== "?" && !label.startsWith("[")) {
      components.add(label);
    }
  }

  // Collect arrow connections
  for (const el of elements) {
    if (el.type !== "arrow") continue;
    const startId = el.startBinding?.elementId;
    const endId = el.endBinding?.elementId;
    const fromEl = startId ? elements.find((e) => e.id === startId) : null;
    const toEl = endId ? elements.find((e) => e.id === endId) : null;
    const fromLabel = fromEl ? getElementLabel(fromEl, idToLabel) : "?";
    const toLabel = toEl ? getElementLabel(toEl, idToLabel) : "?";
    const arrowLabel = el.label?.text?.trim?.();
    const conn = arrowLabel
      ? `${fromLabel} -> ${toLabel} (${arrowLabel})`
      : `${fromLabel} -> ${toLabel}`;
    connections.push(conn);
    if (fromLabel !== "?") components.add(fromLabel);
    if (toLabel !== "?") components.add(toLabel);
  }

  const compList = Array.from(components).filter((c) => c !== "?" && !c.startsWith("["));
  const parts: string[] = [];
  if (compList.length > 0) {
    parts.push(`Components: ${compList.join(", ")}`);
  }
  if (connections.length > 0) {
    parts.push(`Connections: ${connections.join("; ")}`);
  }
  return parts.join("\n");
}

/**
 * Convert diagram data (from Firebase) to agent-readable text.
 * Returns empty string if no meaningful content.
 */
export function diagramDataToText(data: { elements?: unknown } | null | undefined): string {
  if (!data || !data.elements) return "";
  const text = diagramElementsToText(data.elements);
  return text.trim() || "";
}
